import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  query,
  where,
  updateDoc,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Message, User } from '../types';

export interface CreateMessageData {
  content: string;
  receiverId: string;
  receiverName: string;
  receiverEmail: string;
}

export const messageService = {
  // Create a new message
  async createMessage(senderId: string, senderName: string, messageData: CreateMessageData): Promise<string> {
    try {
      const messageDoc = {
        content: messageData.content,
        senderId,
        senderName,
        receiverId: messageData.receiverId,
        receiverName: messageData.receiverName,
        receiverEmail: messageData.receiverEmail,
        isRead: false,
        createdAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, 'messages'), messageDoc);

      // Email notifications are handled by the Firebase Cloud Function
      // (checkUnreadMessages) which runs every 5 minutes and emails
      // recipients of messages that remain unread after 10 minutes.

      return docRef.id;
    } catch (error) {
      console.error('Error creating message:', error);
      throw new Error('Failed to send message');
    }
  },

  // Get conversation between two users
  async getConversation(userId1: string, userId2: string): Promise<Message[]> {
    try {
      // Query without orderBy to avoid index requirements
      const q = query(
        collection(db, 'messages'),
        where('senderId', 'in', [userId1, userId2]),
        where('receiverId', 'in', [userId1, userId2])
      );

      const querySnapshot = await getDocs(q);
      const messages: Message[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Only include messages that are actually between these two users
        if ((data.senderId === userId1 && data.receiverId === userId2) ||
            (data.senderId === userId2 && data.receiverId === userId1)) {
          messages.push({
            id: doc.id,
            content: data.content,
            senderId: data.senderId,
            senderName: data.senderName,
            receiverId: data.receiverId,
            receiverName: data.receiverName,
            isRead: data.isRead,
            createdAt: data.createdAt?.toDate() || new Date(),
          } as Message);
        }
      });

      // Sort in chronological order (oldest first)
      return messages.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    } catch (error) {
      console.error('Error fetching conversation:', error);
      throw new Error('Failed to fetch conversation');
    }
  },

  // Get all conversations for a user
  async getUserConversations(userId: string): Promise<Message[]> {
    try {
      console.log('Fetching conversations for user:', userId);

      // Check if db is available
      if (!db) {
        console.error('Firestore database not initialized');
        return [];
      }

      // Try a simpler approach: get all messages for this user without orderBy
      // This avoids potential index issues
      const qSent = query(
        collection(db, 'messages'),
        where('senderId', '==', userId)
      );

      const qReceived = query(
        collection(db, 'messages'),
        where('receiverId', '==', userId)
      );

      console.log('Executing queries...');
      const [sentSnapshot, receivedSnapshot] = await Promise.all([
        getDocs(qSent),
        getDocs(qReceived)
      ]);

      console.log('Sent messages:', sentSnapshot.size, 'Received messages:', receivedSnapshot.size);

      const messagesMap = new Map<string, Message>();

      // Process sent messages
      sentSnapshot.forEach((doc) => {
        const data = doc.data();
        const partnerId = data.receiverId;
        const message = {
          id: doc.id,
          content: data.content,
          senderId: data.senderId,
          senderName: data.senderName,
          receiverId: data.receiverId,
          receiverName: data.receiverName,
          receiverEmail: data.receiverEmail,
          isRead: data.isRead,
          createdAt: data.createdAt?.toDate() || new Date(),
        } as Message;

        // Keep only the most recent message per conversation partner
        if (!messagesMap.has(partnerId) ||
            (messagesMap.get(partnerId)!.createdAt < message.createdAt)) {
          messagesMap.set(partnerId, message);
        }
      });

      // Process received messages
      receivedSnapshot.forEach((doc) => {
        const data = doc.data();
        const partnerId = data.senderId;
        const message = {
          id: doc.id,
          content: data.content,
          senderId: data.senderId,
          senderName: data.senderName,
          receiverId: data.receiverId,
          receiverName: data.receiverName,
          receiverEmail: data.receiverEmail,
          isRead: data.isRead,
          createdAt: data.createdAt?.toDate() || new Date(),
        } as Message;

        // Keep only the most recent message per conversation partner
        if (!messagesMap.has(partnerId) ||
            (messagesMap.get(partnerId)!.createdAt < message.createdAt)) {
          messagesMap.set(partnerId, message);
        }
      });

      // Convert map to array and sort by most recent
      const messages = Array.from(messagesMap.values())
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      console.log('Returning', messages.length, 'conversations');
      return messages;
    } catch (error) {
      console.error('Error fetching user conversations:', error);
      console.error('Error details:', error);
      // Return empty array instead of throwing error to prevent app crashes
      return [];
    }
  },

  // Get unread messages count for a user
  async getUnreadCount(userId: string): Promise<number> {
    try {
      // Single-field query to avoid composite index requirement
      const q = query(
        collection(db, 'messages'),
        where('receiverId', '==', userId)
      );

      const querySnapshot = await getDocs(q);
      let count = 0;
      querySnapshot.forEach(doc => {
        if (doc.data().isRead === false) count++;
      });
      return count;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }
  },

  // Mark message as read
  async markAsRead(messageId: string): Promise<void> {
    try {
      const messageRef = doc(db, 'messages', messageId);
      await updateDoc(messageRef, {
        isRead: true,
      });
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw new Error('Failed to mark message as read');
    }
  },

  // Mark all messages in a conversation as read
  async markConversationAsRead(userId1: string, userId2: string): Promise<void> {
    try {
      // Use the same two-field 'in' query as getConversation, filter isRead client-side
      const q = query(
        collection(db, 'messages'),
        where('senderId', 'in', [userId1, userId2]),
        where('receiverId', 'in', [userId1, userId2])
      );

      const querySnapshot = await getDocs(q);
      const updatePromises = querySnapshot.docs
        .filter(doc => {
          const data = doc.data();
          // Only mark messages sent TO the current viewer as read
          return data.isRead === false && data.receiverId === userId1;
        })
        .map(doc => updateDoc(doc.ref, { isRead: true }));

      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  },

  // Real-time listener for new messages
  subscribeToMessages(userId: string, callback: (messages: Message[]) => void) {
    // Remove orderBy to avoid index requirements
    const q = query(
      collection(db, 'messages'),
      where('receiverId', '==', userId)
    );

    return onSnapshot(q, (querySnapshot) => {
      const messages: Message[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        messages.push({
          id: doc.id,
          content: data.content,
          senderId: data.senderId,
          senderName: data.senderName,
          receiverId: data.receiverId,
          receiverName: data.receiverName,
          isRead: data.isRead,
          createdAt: data.createdAt?.toDate() || new Date(),
        } as Message);
      });
      callback(messages);
    });
  },

  // Send email notification via Netlify function
  async sendEmailNotification(receiverEmail: string, senderName: string, messageContent: string): Promise<void> {
    try {
      if (!receiverEmail) return;
      await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: receiverEmail,
          senderName,
          messagePreview: messageContent,
        }),
      });
    } catch (error) {
      console.error('Error sending email notification:', error);
      // Don't throw — email failure shouldn't break message sending
    }
  },

  // Get user by ID (for messaging)
  async getUserById(userId: string): Promise<User | null> {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const data = userSnap.data();
        return {
          id: userSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as User;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching user:', error);
      return null;
    }
  },

  // Search users by username or name for starting new conversations
  async searchUsers(searchQuery: string, currentUserId: string): Promise<User[]> {
    try {
      if (!db || !searchQuery.trim()) return [];
      const snapshot = await getDocs(collection(db, 'users'));
      const q = searchQuery.toLowerCase().replace(/^@/, '');
      const results: User[] = [];

      snapshot.forEach((d) => {
        if (d.id === currentUserId) return;
        const data = d.data();
        const username = (data.username || '').toLowerCase();
        const fullName = `${data.firstName || ''} ${data.lastName || ''}`.toLowerCase();
        if (username.includes(q) || fullName.includes(q)) {
          results.push({
            id: d.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as User);
        }
      });

      return results.slice(0, 10);
    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  }
}; 